import { ComponentType, SVGProps } from "react";

import {
    RiChatNewLine,
    RiEditBoxLine,
    RiPriceTag3Line,
    RiEyeLine,
    RiStarLine,
    RiStickyNoteLine,
    RiCheckboxMultipleLine,
    RiUserFollowLine,
    RiListCheck3,
    RiTaskLine,
    RiCloseCircleLine,
    RiShuffleLine,
    RiRobot2Line,
    RiCalendarEventLine,
    RiAlarmLine,
    RiRestartLine,
    RiUserAddLine,
    RiRefundLine,
    RiCalendarCheckLine,
    RiTeamLine,
    RiDiscussLine,
    RiMailSendFill,
    RiUserSharedLine,
    RiIndeterminateCircleLine,
    RiLoopLeftLine,
    RiPauseCircleLine,
    RiBookOpenLine,
    RiChatSmile2Line,
    RiRefreshLine,
    RiFlag2Line,
    RiErrorWarningLine,
    RiAttachmentLine,
    RiChatCheckLine,
    RiBarChartBoxLine,
    RiFileEditLine,
    RiPriceTag2Line,
    RiAlertLine,
    RiArrowUpDoubleLine,
    RiFileAddLine,
    RiFontSize2,
    RiLoopRightLine,
    RiMegaphoneLine,
    RiFocus2Line,
    RiMailLine,
    RiProfileLine,
    RiQuestionAnswerLine,
    RiFlashlightLine,
    RiPlug2Line,
    RiCodeBoxLine,
    RiFilePaperLine,
    RiCheckboxCircleLine,
    RiListUnordered,
    RiForbidLine,
    RemixiconComponentType,
} from "@remixicon/react";

import { TbNumber123 } from "react-icons/tb";
import { ReactComponent as RocketIcon } from "../../../assets/icons/rocket-new.svg";
import StraightThenCurvedEdge from "../components/edges/StraightThenCurvedEdge";
import CurvedEdge from "../components/edges/CurvedEdge";

import BaseActionNode from "../components/nodes/BaseActionNode";
import NewActionNode from "../components/nodes/NewActionNode";
import BaseGroupNode from "../components/nodes/BaseGroupNode";
import SubactionGroupNode from "../components/nodes/SubactionGroupNode";
import EmptyFlowNode from "../components/nodes/EmptyFlowNode";
import InitialActionNode from "../components/nodes/InitialActionNode";
import { ActionContextType } from "../../../__generated__/api";
import GhostActionNode from "../components/nodes/GhostActionNode";
import {
    ACTION, CREATED_ID, CREATING_ID, EDGE, GENERAL_ID, GROUP, NODE_TYPE, WEBHOOK_ID,
} from "./constants";

export const GROUP_ID_TYPE_MAP: Record<string, string> = {
    [GROUP.ACTIONS]: "actions",
    [GROUP.ACTIONS_CREATING]: "actions-creating",
    [GROUP.NO_MATCH]: "no-match",
    [GROUP.MATCH]: "match",
    [GROUP.CONDITION]: "condition",
    [GROUP.START]: "start",
    [GROUP.ON_STATUS_UPDATED]: "on-status-updated",
    [GROUP.WEBHOOK_ACTIONS]: "webhook-actions",
    [GROUP.REJECTED]: "rejected",
    [GROUP.FINISHED]: "finished",
    [GROUP.NOT_APPLICABLE]: "not-applicable",
    [GROUP.MESSAGE_ARRIVES]: "message-arrives",
    [GROUP.SUBACTION]: "subaction",
    [GROUP.DEFAULT]: "default",
};

export const GROUP_ID_ICON_MAP: Record<string, RemixiconComponentType | ComponentType<SVGProps<SVGSVGElement>>> = {
    [GROUP.ACTIONS]: RocketIcon,
    [GROUP.ACTIONS_CREATING]: RocketIcon,
    [GROUP.WEBHOOK_ACTIONS]: RocketIcon,
    [GROUP.MATCH]: RiCheckboxCircleLine,
    [GROUP.FINISHED]: RiCheckboxCircleLine,
    [GROUP.NO_MATCH]: RiCloseCircleLine,
    [GROUP.REJECTED]: RiCloseCircleLine,
    [GROUP.CONDITION]: RiListCheck3,
    [GROUP.SUBACTION]: RiListUnordered,
    [GROUP.NOT_APPLICABLE]: RiForbidLine,
};

export const NODE_TYPES = {
    [ACTION.NEW]: NewActionNode,
    [ACTION.GHOST]: GhostActionNode,
    [ACTION.REQUEST_CREATED]: InitialActionNode,
    [ACTION.REQUEST_CREATING]: InitialActionNode,
    [ACTION.GENERIC_NO_TICKET]: InitialActionNode,
    [NODE_TYPE.ACTION_BASE]: BaseActionNode,
    [NODE_TYPE.EMPTY_FLOW]: EmptyFlowNode,
    [GROUP.ACTIONS]: BaseGroupNode,
    [GROUP.ACTIONS_CREATING]: BaseGroupNode,
    [GROUP.CONDITION]: BaseGroupNode,
    [GROUP.NO_MATCH]: BaseGroupNode,
    [GROUP.MATCH]: BaseGroupNode,
    [GROUP.START]: BaseGroupNode,
    [GROUP.ON_STATUS_UPDATED]: BaseGroupNode,
    [GROUP.WEBHOOK_ACTIONS]: BaseGroupNode,
    [GROUP.DEFAULT]: BaseGroupNode,
    [GROUP.REJECTED]: BaseGroupNode,
    [GROUP.FINISHED]: BaseGroupNode,
    [GROUP.NOT_APPLICABLE]: BaseGroupNode,
    [GROUP.MESSAGE_ARRIVES]: BaseGroupNode,
    [GROUP.SUBACTION]: SubactionGroupNode,
};

export const EDGE_TYPES = {
    [EDGE.CURVED]: CurvedEdge,
    [EDGE.STRAIGHT_THEN_CURVED]: StraightThenCurvedEdge,
};

export const ACTION_ICON_MAP = {
    [ACTION.CREATE_TASK]: RiTaskLine,
    [ACTION.ADD_NOTE]: RiStickyNoteLine,
    [ACTION.REQUEST_CREATED]: RiFlashlightLine,
    [ACTION.REQUEST_CREATING]: RiFlashlightLine,
    [ACTION.GENERIC_NO_TICKET]: RiFilePaperLine,
    [ACTION.CONDITION]: RiListCheck3,
    [ACTION.ASSIGN_TO_QUEUE]: RiCheckboxMultipleLine,
    [ACTION.ASSIGN_TO_USER]: RiUserFollowLine,
    [ACTION.SET_TICKET_STATUS_FINISHED]: RiCloseCircleLine,
    [ACTION.SET_TICKET_STATUS_HOLD]: RiPauseCircleLine,
    [ACTION.SET_TICKET_STATUS_IGNORE]: RiIndeterminateCircleLine,
    [ACTION.SET_TICKET_STATUS_REOPEN]: RiBookOpenLine,
    [ACTION.SET_TICKET_PRIORITY]: RiArrowUpDoubleLine,
    [ACTION.SET_TICKET_SEVERITY]: RiAlertLine,
    [ACTION.SET_FIELD_VALUE]: RiEditBoxLine,
    [ACTION.MAP]: RiShuffleLine,
    [ACTION.ADD_MESSAGE]: RiChatNewLine,
    [ACTION.ADD_PARTICIPANT]: RiUserAddLine,
    [ACTION.TRIGGER]: RiFlashlightLine,
    [ACTION.TRIGGER_CONNECTOR]: RiPlug2Line,
    [ACTION.SET_TICKET_FIELD_VALUE]: RiCheckboxMultipleLine,
    [ACTION.DELAY_TIME]: RiCalendarEventLine,
    [ACTION.SET_TICKET_SLA_TIER]: RiAlarmLine,
    [ACTION.LOOP]: RiLoopLeftLine,
    [ACTION.LOOP_MULTIPLE_CHOICE]: RiLoopRightLine,
    [ACTION.FORMAT]: RiFontSize2,
    [ACTION.ON_STATUS_UPDATED]: RiFlag2Line,
    [ACTION.WEBHOOK]: RiCodeBoxLine,
    [ACTION.IS_DATE_IN_HOURS_OF_OPERATION]: RiCalendarCheckLine,
    [ACTION.SET_TICKET_STATE]: RiBookOpenLine,
    [ACTION.ON_FIELD_UPDATED]: RiEditBoxLine,
    [ACTION.SET_TICKET_TAGS]: RiPriceTag2Line,
    [ACTION.SET_REQUESTOR]: RiUserSharedLine,
    [ACTION.IS_USER_IN_GROUP]: RiTeamLine,
    [ACTION.ON_NEW_MESSAGE]: RiChatCheckLine,
    [ACTION.ON_DUE_DATE_EXPIRED]: RiAlarmLine,
    [ACTION.RUN_DEFLECTION]: RiRobot2Line,
    [ACTION.SEND_FEEDBACK]: RiChatSmile2Line,
    [ACTION.RUN_ACTION]: RiFocus2Line,
    [ACTION.SET_REQUEST_TYPE]: RiFileEditLine,
    [ACTION.SEND_EMAIL]: RiMailLine,
    [ACTION.ALLOW_RESTART]: RiRestartLine,
    [ACTION.CREATE_TICKET]: RiFileAddLine,
    [ACTION.ON_FEEDBACK]: RiChatSmile2Line,
    [ACTION.SEND_QUESTION]: RiQuestionAnswerLine,
    [ACTION.FILL_OUT_FORM]: RiProfileLine,
    [ACTION.ON_STATUS_REOPENED]: RiRefreshLine,
    [ACTION.ON_ERROR]: RiErrorWarningLine,
    [ACTION.EXECUTE_WITH_COUNT]: TbNumber123,
    [ACTION.ON_EXTERNAL_TICKET]: RiAttachmentLine,
    [ACTION.UNASSIGN]: RiRefundLine,
    [ACTION.SET_METRIC]: RiBarChartBoxLine,
    [ACTION.GET_USER_BY_EMAIL]: RiMailSendFill,
    [ACTION.START_CONVERSATION]: RiChatNewLine,
    [ACTION.CHANNEL_SET_TAGS]: RiPriceTag3Line,
    [ACTION.ADD_PARTICIPANT_THREAD]: RiDiscussLine,
    [ACTION.MAP_CHANNEL_MESSAGES]: RiMegaphoneLine,
    [ACTION.FIELD_DISPLAY]: RiEyeLine,
    [ACTION.FIELD_REQUIRED]: RiStarLine,
};

export const ACTION_TYPE_MAP: Record<string, ActionContextType> = {
    [CREATING_ID]: ActionContextType.Request,
    [CREATED_ID]: ActionContextType.Ticket,
    [GENERAL_ID]: ActionContextType.GenericNoTicket,
    [WEBHOOK_ID]: ActionContextType.Webhook,
};

export function getGroupEdgeType(nodeType: string) {
    switch (nodeType) {
    case GROUP.ACTIONS:
    case GROUP.ACTIONS_CREATING:
    case GROUP.WEBHOOK_ACTIONS:
    case GROUP.ACTIONS_WORKSPACE:
    case GROUP.SUBACTION:
    case GROUP.CONDITION:
        return EDGE.STRAIGHT;
    default:
        return EDGE.CURVED;
    }
}
